"use client";

import { Layers, Network, PanelLeft } from "lucide-react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { IntentPrefetchLink } from "@/components/navigation/IntentPrefetchLink";
import { Button } from "@/components/ui/button";
import {
  SidebarContent,
  SidebarHeader,
  Sidebar as SidebarPrimitive,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { strings } from "@/content/strings";
import { routes } from "@/lib/routes";
import { cn } from "@/lib/utils";

// Nav stays lean on purpose: items are added only when a real page ships
// behind them (docs/PROJECT-STRUCTURE.md: Route Conventions).
const navItems = [
  {
    href: routes.systemOverview(),
    activePath: routes.system(),
    label: strings.navigation.system,
    icon: Network,
  },
  {
    href: routes.levels(),
    activePath: routes.levels(),
    label: strings.navigation.levels,
    icon: Layers,
  },
];

function isActive(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

export function Sidebar() {
  const pathname = usePathname();
  const { state, isMobile, openMobile, setOpenMobile, toggleSidebar } =
    useSidebar();
  const collapsed = !isMobile && state === "collapsed";

  return (
    <SidebarPrimitive
      collapsible="icon"
      className="z-30 border-sidebar-border bg-sidebar"
    >
      <SidebarHeader
        className={cn(
          "border-sidebar-border border-b px-3 py-4",
          collapsed && "items-center px-2",
        )}
      >
        <div
          className={cn(
            "flex items-center gap-2",
            collapsed ? "flex-col" : "justify-between",
          )}
        >
          <IntentPrefetchLink
            href={routes.system()}
            onNavigate={() => setOpenMobile(false)}
            className={cn(
              "flex min-w-0 items-center",
              collapsed
                ? "size-9 justify-center rounded-xl bg-primary/10 text-primary"
                : "px-1",
            )}
          >
            {collapsed ? (
              <>
                <span
                  aria-hidden
                  className="font-heading font-extrabold text-sm"
                >
                  {strings.appName.charAt(0)}
                </span>
                <span className="sr-only">{strings.appName}</span>
              </>
            ) : (
              <Image
                src="/brand/logo.png"
                alt={strings.appName}
                width={160}
                height={50}
                priority
              />
            )}
          </IntentPrefetchLink>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-expanded={isMobile ? openMobile : !collapsed}
            aria-label={strings.ui.sidebar.toggle}
            onClick={toggleSidebar}
            className="rounded-full"
          >
            <PanelLeft
              className={cn(
                "size-4 transition-transform",
                collapsed && "rotate-180",
              )}
              aria-hidden
            />
          </Button>
        </div>
      </SidebarHeader>

      <SidebarContent
        className={cn("px-3 py-4", collapsed && "items-center px-2")}
      >
        <nav
          className={cn(
            "flex w-full flex-col gap-1",
            collapsed && "items-center",
          )}
        >
          {navItems.map((item) => {
            const active = isActive(pathname, item.activePath);
            const navLink = (
              <Button
                asChild
                variant={active ? "secondary" : "ghost"}
                size={collapsed ? "icon-lg" : "lg"}
                className={cn(
                  "rounded-xl text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-foreground",
                  active && "interaction-selected",
                  collapsed ? "size-9 px-0" : "w-full justify-start gap-3 px-3",
                )}
              >
                <IntentPrefetchLink
                  href={item.href}
                  prefetch={true}
                  onNavigate={() => setOpenMobile(false)}
                  aria-current={active ? "page" : undefined}
                >
                  <item.icon className="size-4.5" aria-hidden />
                  <span className={cn("truncate", collapsed && "sr-only")}>
                    {item.label}
                  </span>
                </IntentPrefetchLink>
              </Button>
            );

            if (collapsed) {
              return (
                <Tooltip key={item.href}>
                  <TooltipTrigger asChild>{navLink}</TooltipTrigger>
                  <TooltipContent side="right">{item.label}</TooltipContent>
                </Tooltip>
              );
            }

            return (
              <div key={item.href} className="w-full">
                {navLink}
              </div>
            );
          })}
        </nav>
      </SidebarContent>
    </SidebarPrimitive>
  );
}
